import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/react-hooks';
import { withRouter } from 'react-router-dom';
import { injectIntl, defineMessages } from 'react-intl';
import { withFormik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import { withGrowl } from '../../components/GrowlNotifications';
import { intlShape, historyShape, growlShape } from '../../components/prop-types';
import Debug from '../../components/Debug';
import { validationMessages, notificationMessages } from '../../i18n/common';
import { InputPasswordWithValidationMessage } from '../../components/prime-formik';

const messages = defineMessages({
  cancel: { id: 'actions.cancel', defaultMessage: 'Cancel' },
  submit: { id: 'actions.submit', defaultMessage: 'Submit' },
  changePassword: {
    id: 'users.change-password.heading',
    defaultMessage: 'Change password',
  },
  currentPassword: {
    id: 'users.change-password.current-passowrd',
    defaultMessage: 'Curent password',
  },
  newPassword: {
    id: 'users.change-password.new-passowrd',
    defaultMessage: 'New password',
  },
  passwordTooShort: {
    id: 'password_validation.minimum_length',
    defaultMessage: 'Password must be at least 12 characters long',
  },
  passwordTooLong: {
    id: 'password_validation.maximum_length',
    defaultMessage: 'Invalid password',
  },
  passwordSameAsOldPassword: {
    id: 'password_validation.new_password_same_as_old_one',
    defaultMessage: 'New password cannot be the same as the current password',
  },
  confirmNewPassword: {
    id: 'users.change-password.confirm-new-passowrd',
    defaultMessage: 'Confirm new password',
  },
  passwordRequired: {
    id: 'login.password_required',
    defaultMessage: 'Password is required',
  },
  passwordConfirmationRequired: {
    id: 'login.password_confirmation_required',
    defaultMessage: 'Password confirmation is required',
  },
  passwordConfirmationDoesNotMatch: {
    id: 'login.password_confirmation_does_not_match',
    defaultMessage: 'Password confirmation does not match',
  },
});

const CHANGE_OWN_PASSWORD = gql`
  mutation changeOwnPassword($input: ChangeOwnPasswordInput!) {
    changeOwnPassword(input: $input) {
        userId,
        accountId
    }
  }
`;

function ChangeOwnPasswordForm(props) {
  const {
    accountId,
    userId,
    intl: { formatMessage },
    toggleChangePassword,
  } = props;

  return (
    <div className="p-grid">
      <div className="p-sm-12 p-md-6">
        <Form>
          <input type="hidden" name="accountId" value={accountId} />
          <input type="hidden" name="userId" value={userId} />
          <div className="p-grid">
            <div className="p-col-12">
              <Field
                id="currentPassword"
                name="currentPassword"
                labelText={formatMessage(messages.currentPassword)}
                component={InputPasswordWithValidationMessage}
              />
            </div>
          </div>
          <div className="p-grid">
            <div className="p-col-12">
              <Field
                id="newPassword"
                name="newPassword"
                labelText={formatMessage(messages.newPassword)}
                component={InputPasswordWithValidationMessage}
              />
            </div>
          </div>
          <div className="p-grid">
            <div className="p-col-12">
              <Field
                id="confirmPassword"
                name="confirmPassword"
                labelText={formatMessage(messages.confirmNewPassword)}
                component={InputPasswordWithValidationMessage}
              />
            </div>
          </div>
          <div className="p-grid">
            <div className="p-col-12">
              <Button
                type="button"
                className="p-button-secondary"
                label={formatMessage(messages.cancel)}
                onClick={() => toggleChangePassword(false)}
              />
              <Button type="submit" label={formatMessage(messages.submit)} />
            </div>
          </div>
          <Debug />
        </Form>
      </div>
    </div>
  );
}

ChangeOwnPasswordForm.propTypes = {
  accountId: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired,
  intl: intlShape.isRequired,
  toggleChangePassword: PropTypes.func.isRequired,
};

async function handleSubmit(values, { props, setSubmitting }) {
  const {
    changePasswordMutation,
    growl,
    intl: { formatMessage },
    toggleChangePassword,
  } = props;

  const {
    accountId,
    userId,
    currentPassword,
    newPassword,
  } = values;

  try {
    await changePasswordMutation({
      variables: {
        input: {
          accountId,
          userId,
          currentPassword,
          newPassword,
        },
      },
    });
    setSubmitting(false);
    growl.show({ severity: 'info', detail: formatMessage(notificationMessages.success), summary: 'Info Message' });
    toggleChangePassword(false);
  } catch (error) {
    growl.show({
      severity: 'error', detail: error.message, sticky: true,
    });
  }
}

function buildValidationSchema(formatMessage) {
  return Yup.object().shape({
    accountId: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
    userId: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
    currentPassword: Yup.string().required(formatMessage(messages.passwordRequired)),
    newPassword: Yup.string()
      .when('currentPassword', {
        is: val => (!!(val && val.length > 0)),
        then: Yup.string().notOneOf(
          [Yup.ref('currentPassword')],
          formatMessage(messages.passwordSameAsOldPassword),
        ),
      })
      .min(12, formatMessage(messages.passwordTooShort))
      .max(100, formatMessage(messages.passwordTooLong))
      .required(formatMessage(messages.passwordRequired)),
    confirmPassword: Yup.string().when('newPassword', {
      is: val => (!!(val && val.length > 0)),
      then: Yup.string().oneOf(
        [Yup.ref('newPassword')],
        formatMessage(messages.passwordConfirmationDoesNotMatch),
      ),
    }).required(formatMessage(messages.passwordConfirmationRequired)),
  });
}

function mapPropsToValues({ intl: { formatMessage }, accountId, userId }) {
  // Formik requires initial values for each field, to support error messages on submit
  const validationSchema = buildValidationSchema(formatMessage);

  const initialValues = {
    ...validationSchema.cast(),
    accountId,
    userId,
  };
  return initialValues;
}

const ChangeOwnPasswordFormEnhancedForm = withFormik({
  validationSchema: ({ intl: { formatMessage } }) => buildValidationSchema(formatMessage),
  mapPropsToValues,
  handleSubmit,
})(ChangeOwnPasswordForm);

function UserChangePassword(props) {
  const {
    intl: { formatMessage },
  } = props;

  const [changePasswordMutation] = useMutation(CHANGE_OWN_PASSWORD);

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <Panel header={formatMessage(messages.changePassword)}>
          <ChangeOwnPasswordFormEnhancedForm
            changePasswordMutation={changePasswordMutation}
            {...props}
          />
        </Panel>
      </div>
    </div>
  );
}

UserChangePassword.propTypes = {
  accountId: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired,
  intl: intlShape.isRequired,
  history: historyShape.isRequired,
  growl: growlShape.isRequired,
};

export default injectIntl(withRouter((withGrowl(UserChangePassword))));
